import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import BadgeList from 'src/components/badge-list';
import type { useGetAuthUsers } from 'src/hooks/queries/user/useGetAuthUsers';

import Link from '@/components/link';
import { useIsCustomerSupport } from '@/hooks/useIsCustomerSupport';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import type { CollectionData } from '@/utils/collectionUtils';
import { useGetTableProps } from '@/utils/table/hooks/useGetTableProps';
import type { TableFields } from '@/utils/table/types';
import { dateColumnFromConfig } from '@/utils/table/utils/dateColumn';

import type { SupportAccessStatus } from './supportAccess';
import { getSupportAccessStatus } from './supportAccess';
import UserStatusCell from './UserStatusCell';

type GetAuthUsersData = NonNullable<ReturnType<typeof useGetAuthUsers>['data']>;

type AuthUserFields = CollectionData<GetAuthUsersData['auth_user'][0]>;

// `RoleKey` and `LastSeen` are no longer `Omit`ted: COI-927 and COI-830
// removed both top-level selections from `getAuthUsers.graphql`, so there is
// nothing left on the query shape to omit — they exist only as the mapped
// fields declared below.
type AuthUserRegisterFields = Omit<AuthUserFields, 'userGroupUsers'> & {
  Name: string;
  UserGroups: string[];
  OrgUserExternalId: string | null;
  /**
   * COI-714: a MAPPED field now, like `Name` and `UserGroups` above — no
   * longer a passthrough of the query's top-level `Status`. `auth.user`'s
   * tenant-global column is deprecated, so this is flattened from the
   * caller org's `organisationusers` membership row in `mappedData` below.
   */
  Status: string | null | undefined;
  /**
   * COI-824 / COI-927 / COI-830: mapped from the membership row too, for the
   * same reason as `Status`. Neither is selected at the top level of
   * `getAuthUsers.graphql` any more, so there is no inherited query field to
   * `Omit` or collide with.
   *
   * Both are `undefined`-able and must SAY so. `organisationusers?.[0]` is
   * absent whenever the caller has no membership row, so the mapping yields
   * `undefined` — and the `as AuthUserRegisterFields` assertion in `mappedData`
   * would otherwise hide that from every consumer. `LastSeen` in particular was
   * declared a bare `string`, which is how a possibly-absent date reached
   * `dateColumnFromConfig` claiming it could never be missing.
   */
  RoleKey: string | null | undefined;
  LastSeen: string | null | undefined;
  /**
   * COI-819: derived, not queried — the two columns the DB's entitlement rule
   * reads (`auth.user."IsCustomerSupport"` and the membership `RoleKey`)
   * collapsed into the four states the UI has to distinguish. Computed in
   * `mappedData` so the cell, the export and the modal all describe the same
   * user the same way.
   */
  SupportAccess: SupportAccessStatus;
};

export const useGetUsersTableProps = (
  users: AuthUserFields[],
  onEdit: (user: AuthUserRegisterFields) => void
) => {
  const { t } = useTranslation(['common'], { keyPrefix: 'userSettings' });
  const { t: st } = useTranslation(['common'], {
    keyPrefix: 'userSettings.columns',
  });

  // Check if the new granular role system (trpc feature) is enabled
  const isMultiRoleEnabled = useIsFeatureFlagEnabled('trpc');

  // Resolved server-side from `auth.user_role` on V3 — see
  // `useIsCustomerSupport`. Fails closed while in flight, so the column appears
  // a beat after load rather than flashing for a customer admin.
  const callerIsCS = useIsCustomerSupport();

  const showSupportAccessColumn = isMultiRoleEnabled && callerIsCS;

  const fields: TableFields<AuthUserRegisterFields> = {
    Name: {
      id: 'name',
      header: st('displayName'),
      cell: (item) => (
        <Link variant={'secondary'} onFollow={() => onEdit(item)}>
          {item.Name}
        </Link>
      ),
    },
    FirstName: {
      header: st('firstName'),
    },
    LastName: {
      header: st('lastName'),
    },
    FriendlyName: {
      header: st('username'),
    },
    Email: {
      header: st('email'),
    },
    RoleKey: {
      header: st('role'),
      cell: (item) => {
        if (isMultiRoleEnabled && item.customRoles) {
          // When TRPC is enabled, show roles from custom_role table
          const roleNames = item.customRoles.map(
            (userRole) => userRole.role.RoleName
          );

          return roleNames.join(', ');
        }
        if (!isMultiRoleEnabled && item.organisationusers?.length > 0) {
          // When TRPC is not enabled, show role from organisation_users table
          return item.organisationusers[0].RoleKey;
        }

        // Fallback for older data or if no roles are available
        return '';
      },
      exportVal: (item) => {
        if (isMultiRoleEnabled && item.customRoles) {
          const roleNames = item.customRoles.map(
            (userRole) => userRole.role.RoleName
          );

          return roleNames.join(', ');
        }
        if (!isMultiRoleEnabled && item.organisationusers?.length > 0) {
          // When TRPC is not enabled, show role from organisation_users table
          return item.organisationusers[0].RoleKey;
        }

        // Fallback for older data or if no roles are available
        return '';
      },
    },
    /**
     * COI-819 — who at RiskSmart can reach this organisation's data, and who
     * only looks like they can.
     *
     * V3 only, matching `shouldShowSupportAccess`. On V2 the membership role IS
     * the user's role, so the `Role` column beside this one already renders
     * `CustomerSupport` verbatim — a second column restating it would be noise,
     * and the modal switch that acts on it is not on that branch either.
     * Verified in a browser: with the flag off, this column rendered next to a
     * `Role` column already showing the same fact.
     *
     * RiskSmart-internal callers only, for the same reason the modal field is —
     * see `shouldShowSupportAccess`. Keeping the two gates in step is what stops
     * a customer admin meeting a column whose four states nothing in their UI
     * lets them act on. The column is also dropped from the CSV/PDF export by
     * the same condition, since `exportVal` lives on the field config.
     *
     * `none` renders empty rather than "No": a column of "No" against every
     * ordinary member is noise, and the states worth reading are the three that
     * are not `none`.
     */
    ...(showSupportAccessColumn
      ? {
          SupportAccess: {
            header: st('supportAccess'),
            cell: (item) =>
              item.SupportAccess === 'none' ? (
                ''
              ) : (
                <BadgeList
                  badges={[t(`supportAccessStatus.${item.SupportAccess}`)]}
                />
              ),
            exportVal: (item) =>
              item.SupportAccess === 'none'
                ? ''
                : t(`supportAccessStatus.${item.SupportAccess}`),
          },
        }
      : {}),
    Status: {
      header: st('status'),
      cell: (item) => (
        <UserStatusCell
          userId={item.Id}
          status={
            (item.Status ?? null) as 'active' | 'archived' | null | undefined
          }
          userName={item.Name}
          orgUserExternalId={item.OrgUserExternalId}
          roleKey={item.RoleKey}
        />
      ),
      exportVal: (item) => item.Status ?? '',
    },
    UserGroups: {
      header: st('userGroups'),
      cell: (item) => <BadgeList badges={item.UserGroups} />,
      exportVal: (item) => item.UserGroups.join(', '),
    },
    CreatedOn: dateColumnFromConfig({
      header: { header: st('created_on') },
      dateField: 'CreatedOn',
    }),
    LastSeen: dateColumnFromConfig({
      header: { header: st('lastSeen') },
      dateField: 'LastSeen',
    }),
    Id: {
      header: st('userId'),
    },
    JobTitle: {
      header: st('jobTitle'),
    },
    Department: {
      header: st('department'),
    },
    OfficeLocation: {
      header: st('officeLocation'),
    },
    CreatedByUser: {
      header: st('createdByUser'),
    },
    ModifiedByUser: {
      header: st('modifiedByUser'),
    },
    ModifiedAtTimestamp: dateColumnFromConfig({
      header: { header: st('modifiedAtTimestamp') },
      dateField: 'ModifiedAtTimestamp',
    }),
  };

  // COI-819: no longer filtered by the caller's own CustomerSupport status.
  //
  // The list used to drop every `IsCustomerSupport` user unless the viewer was
  // one, which hid RiskSmart staff from the customer whose data they can reach —
  // and hid nothing at all from the population that actually caused confusion, a
  // membership carrying the support role with no RiskSmart identity behind it
  // (`IsCustomerSupport` false, so never filtered, and never labelled either).
  // Rows stay unfiltered so the list is one honest membership list for everyone;
  // the entitlement STATE those rows carry is what is now RiskSmart-internal —
  // the `SupportAccess` column above renders only for a CS caller, and the
  // status is still computed for every row because that column and the modal
  // must describe the same user the same way.
  const mappedData = useMemo<AuthUserRegisterFields[]>(
    () =>
      users.map((user) => {
        // COI-832: the caller org's display name, falling back to the
        // tenant-global column exactly as `risksmart.user_view_active` now
        // does — `COALESCE(o."DisplayName", u."FriendlyName") AS "FriendlyName"`.
        // The global arm is `FriendlyName`, NOT the global `DisplayName`:
        // `auth.user."FriendlyName"` is a generated column over
        // `DisplayName` → first/last name → `UserName` → `Email`, so it is the
        // wider value and reaching for `DisplayName` first would narrow it.
        const displayName =
          user.organisationusers?.[0]?.DisplayName ?? user.FriendlyName;

        return {
          ...user,
          Name: displayName,
          /**
           * COI-832: org-first with a fallback to the tenant-global column,
           * mirroring the view's `COALESCE(o.col, u.col)` so the two legs of
           * this read agree. Deliberately UNLIKE `RoleKey` and `Status` below,
           * which are org-only with NO fallback: those globals were backfilled
           * and their view arms are now bare, whereas these four are still
           * dual-written mid-migration, so a residual NULL here means "this org
           * has recorded nothing yet" rather than "cleared".
           */
          FriendlyName: displayName,
          JobTitle: user.organisationusers?.[0]?.JobTitle ?? user.JobTitle,
          Department:
            user.organisationusers?.[0]?.Department ?? user.Department,
          OfficeLocation:
            user.organisationusers?.[0]?.OfficeLocation ?? user.OfficeLocation,
          UserGroups: user.userGroupUsers.map((u) => u.userGroups.Name),
          // COI-714 (`Status`) and COI-824 (`RoleKey`, `LastSeen`):
          // org-scoped only. The old `?? user.RoleKey` / `?? user.LastSeen`
          // / `?? user.Status` fallbacks reached for the tenant-global
          // `auth.user` columns, which are last-writer-wins across orgs and
          // are being decommissioned. On `RoleKey` that fallback is the
          // mechanism by which a role set in one federated org appeared in
          // another org's settings table (COI-566).
          //
          // This table went org-scoped-only AHEAD of
          // `risksmart.user_view_active`, which kept a
          // `COALESCE(o.col, u.col)` because it feeds every owner/contributor
          // picker in the app and a residual NULL there would remove
          // colleagues from pickers — whereas here a blank cell honestly
          // reports that the org has recorded nothing rather than borrowing
          // another org's value. The view has since caught up on both
          // columns (COI-927 for `RoleKey`, COI-830 for `LastSeen`), so the
          // two now agree; keep them agreeing.
          //
          // `[0]` is the caller org's membership on BOTH data paths, for
          // two different reasons — check both before changing either:
          //
          //  - Hasura/GraphQL: `read:organisationuser`'s permission filter
          //    is `OrgKey._eq: X-Hasura-org-id`, and the PK is
          //    `(OrgKey, User_Id)`, so the relation can only ever hold the
          //    caller org's single row.
          //  - tRPC/Drizzle: `getAuthUsersQueryConfig` does NOT scope the
          //    relation (it doesn't even select `OrgKey`). The scoping is
          //    applied by the repository, and NOT by overriding the
          //    relation's `where` — `getAll` does not issue a relational
          //    query at all. It selects flat from `organisationuser`
          //    `innerJoin`ed to `user` under `visibleOrgMembership(orgKey)`
          //    and assembles the single-element `organisationusers` array in
          //    JS, so the membership root IS the org predicate. See
          //    `services/data-layer/src/repositories/user-repository.ts`.
          //    RLS would narrow it too (`own_org_data_layer` is granted
          //    `TO data_layer`), but do not rely on that — an earlier
          //    revision of this comment claimed the opposite, having read
          //    it off the hand-maintained Drizzle schema mirror, which
          //    omits that policy (COI-718). The repository's explicit
          //    `where` is what this `[0]` depends on.
          RoleKey: user.organisationusers?.[0]?.RoleKey,
          LastSeen: user.organisationusers?.[0]?.LastSeen,
          Status: user.organisationusers?.[0]?.Status,
          // COI-827: org-scoped only, for the same reason as `Status`
          // above. The tenant-global `auth.user."External_Id"` is no
          // longer selected by either data path.
          OrgUserExternalId: user.organisationusers?.[0]?.External_Id ?? null,
          SupportAccess: getSupportAccessStatus({
            targetIsCustomerSupport: user.IsCustomerSupport,
            membershipRoleKey: user.organisationusers?.[0]?.RoleKey,
          }),
        } as AuthUserRegisterFields;
      }),
    [users]
  );

  return useGetTableProps({
    tableId: 'userRegister',
    data: mappedData,
    entityLabel: t('entity_name'),
    fields,
    initialColumns: [
      'Name',
      'Email',
      'Status',
      'RoleKey',
      ...(showSupportAccessColumn ? (['SupportAccess'] as const) : []),
      'LastSeen',
      'UserGroups',
    ],
    preferencesStorageKey: 'AuthUsersRegisterTable-Preferences',
    customAttributeFormIds: [],
  });
};
